import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Spinner, Card } from 'react-bootstrap';
import { ForumCategory } from 'services/forumService'; // Import Category type
import { createTopic } from 'services/forumService'; // Import the service function

interface CreateTopicFormProps {
  categories: ForumCategory[]; // Needed for category dropdown
  selectedCategoryId: number | null; // Pre-selected category from ForumPage
  onCancel: () => void; // Function to call when cancel button is clicked
  onSubmitSuccess: (newTopicId: number) => void; // Function to call after successful submission
}

const CreateTopicForm: React.FC<CreateTopicFormProps> = ({ 
  categories, 
  selectedCategoryId, 
  onCancel, 
  onSubmitSuccess 
}) => {
  const [title, setTitle] = useState<string>('');
  const [content, setContent] = useState<string>('');
  // Use the passed-in category ID, default to the first category if 'All Posts' was selected
  const [categoryId, setCategoryId] = useState<number | string>(() => {
    return selectedCategoryId ?? (categories.length > 0 ? categories[0].id : '');
  });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Update internal categoryId if the prop changes OR categories load
  useEffect(() => {
      // Only update if categories are available
      if (categories.length > 0) {
         // If a specific category was selected on the main page, use it
         // Otherwise, default to the first available category
         setCategoryId(selectedCategoryId ?? categories[0].id);
      } else {
         // If categories become empty (shouldn't normally happen), clear selection
         setCategoryId(''); 
      }
  }, [selectedCategoryId, categories]); // Rerun when props change

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitError(null);
    setIsSubmitting(true);

    const numericCategoryId = Number(categoryId); // Ensure categoryId is a number

    if (!title.trim() || !content.trim() || isNaN(numericCategoryId)) {
      setSubmitError('Title, content, and a valid category are required.');
      setIsSubmitting(false);
      return;
    }

    try {
      // Call the imported createTopic service function
      const newTopic = await createTopic({ 
          title: title.trim(), 
          content: content.trim(), 
          categoryId: numericCategoryId 
      });
      
      onSubmitSuccess(newTopic.id); // Pass the new topic ID back
      
      // Optionally reset form here if onSubmitSuccess doesn't unmount/hide it
      // setTitle('');
      // setContent('');
    } catch (error: any) {
      console.error("Error creating topic:", error);
      setSubmitError(error.message || 'Failed to create topic. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      {submitError && <Alert variant="danger">{submitError}</Alert>}

      <Form.Group className="mb-3" controlId="createTopicForm.Title">
        <Form.Label>Title</Form.Label>
        <Form.Control
          type="text"
          placeholder="Enter a descriptive title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          disabled={isSubmitting}
        />
      </Form.Group>

      <Form.Group className="mb-3" controlId="createTopicForm.Category">
        <Form.Label>Category</Form.Label>
        <Form.Select 
          aria-label="Select category"
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
          required
          disabled={isSubmitting || categories.length === 0}
        >
          {categories.length === 0 && <option>Loading categories...</option>}
          {/* Ensure value is string for Form.Select */}
          {categories.map(cat => (
            <option key={cat.id} value={cat.id.toString()}>
              {cat.name}
            </option>
          ))}
        </Form.Select>
      </Form.Group>

      <Form.Group className="mb-3" controlId="createTopicForm.Content">
        <Form.Label>Content</Form.Label>
        <Form.Control
          as="textarea"
          rows={5}
          placeholder="Write your post content here..."
          value={content}
          onChange={(e) => setContent(e.target.value)}
          required
          disabled={isSubmitting}
        />
        {/* Optional: Add Markdown preview or editor here */}
      </Form.Group>

      <div className="d-flex justify-content-end">
        <Button 
          variant="secondary" 
          onClick={onCancel} 
          className="me-2" 
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button variant="primary" type="submit" disabled={isSubmitting || !title || !content || !categoryId}>
          {isSubmitting ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'Create Post'}
        </Button>
      </div>
    </Form>
  );
};

export default CreateTopicForm; 